// Bottom corners: health, weapon / ammo / reload ring / equipment, and the reload hint.

import { bindingLabel } from "../settings/keybinds";
import type { Settings } from "../settings/schema";
import { div, setClass, setHidden, span } from "./dom";
import { clamp01 } from "./format";
import { equipmentIcon, icon } from "./icons";
import type { HudFrame } from "./types";

/** At or below this health fraction the bar pulses red and the screen edge vignette shows. */
export const LOW_HEALTH_FRAC = 0.3;
/** At or below this fraction of the largest magazine seen for a weapon the ammo count turns red. */
export const LOW_AMMO_FRAC = 0.25;

const RING_LEN = 2 * Math.PI * 19;
const RELOAD_RING =
  '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">' +
  '<circle class="hud-reload__track" cx="24" cy="24" r="19"/>' +
  '<path class="hud-reload__icon" d="M24 13.5c-2.3 2.3-3.4 4.6-3.4 7.3V32h6.8V20.8c0-2.7-1.1-5-3.4-7.3zM20 34.5h8" />' +
  `<circle class="hud-reload__arc" cx="24" cy="24" r="19" transform="rotate(-90 24 24)" stroke-dasharray="${RING_LEN.toFixed(2)}" stroke-dashoffset="${RING_LEN.toFixed(2)}"/></svg>`;

class EquipChip {
  readonly root = div("hud-equip");
  private readonly iconBox = span("hud-equip__icon");
  private readonly countEl = span("hud-equip__count");
  private readonly keyEl = span("hud-equip__key");
  private id: string | null | undefined = undefined;
  private count = -1;

  constructor() {
    this.root.append(this.keyEl, this.iconBox, this.countEl);
    this.root.hidden = true;
  }

  setKey(label: string): void {
    if (this.keyEl.textContent !== label) this.keyEl.textContent = label;
    setHidden(this.keyEl, label === "");
  }

  update(id: string | null, count: number): void {
    if (id !== this.id) {
      this.id = id;
      setHidden(this.root, id === null);
      if (id !== null)
        this.iconBox.replaceChildren(icon(equipmentIcon(id), "hud-equip__svg"));
    }
    if (count !== this.count) {
      this.count = count;
      this.countEl.textContent = String(count);
      setClass(this.root, "is-empty", count <= 0);
    }
  }
}

export class Vitals {
  /** Bottom-left block. */
  readonly health = div("hud-health");
  /** Bottom-right block. */
  readonly weapon = div("hud-weapon");
  /** Centered "RELOAD" / "NO AMMO" hint. */
  readonly hint = div("hud-hint");

  private readonly hpNum = div("hud-health__num");
  private readonly hpFill = div("hud-health__fill");
  private readonly hpTrail = div("hud-health__trail");
  private readonly nameEl = div("hud-weapon__name");
  private readonly slotEl = span("hud-weapon__slot");
  private readonly ammoBox = div("hud-ammo");
  private readonly magEl = span("hud-ammo__mag");
  private readonly reserveEl = span("hud-ammo__reserve");
  private readonly ring = div("hud-reload");
  /** One cell per round of a full magazine; spent rounds go dark. */
  private readonly pipsEl = div("hud-ammo__pips");
  private readonly ringArc: SVGElement;
  private readonly lethal = new EquipChip();
  private readonly tactical = new EquipChip();
  /** Spawn protection: a badge with a draining bar under the health bar. */
  private readonly shieldEl = div("hud-shield");
  private readonly shieldFill = div("hud-shield__fill");

  private hp = -1;
  private hpFrac = -1;
  private low = false;
  private wName: string | null = null;
  private wSlot = -1;
  private ammo = -1;
  private reserve = -1;
  private reloading = false;
  private reloadQ = -1;
  private ammoLow = false;
  private ammoEmpty = false;
  private melee = false;
  private hintText = "";
  private maxAmmo = 0;
  private readonly maxByWeapon = new Map<string, number>();
  private pips: HTMLElement[] = [];
  private pipMag = -1;
  private pipsLit = 0;
  private shieldQ = -1;

  constructor(private readonly stateRoot: HTMLElement) {
    const bar = div("hud-health__bar");
    bar.append(this.hpTrail, this.hpFill);
    const lbl = div("hud-health__label", "HEALTH");
    const top = div("hud-health__top");
    top.append(this.hpNum, lbl);
    this.health.append(top, bar);
    const shieldBar = div("hud-shield__bar");
    shieldBar.append(this.shieldFill);
    this.shieldEl.append(
      div("hud-shield__label", "SPAWN PROTECTED"),
      shieldBar,
    );
    this.shieldEl.hidden = true;
    this.health.append(this.shieldEl);

    this.ring.innerHTML = RELOAD_RING;
    this.ringArc = this.ring.querySelector(".hud-reload__arc") as SVGElement;
    const counts = div("hud-ammo__counts");
    counts.append(this.magEl, this.reserveEl);
    this.ammoBox.append(this.ring, counts);
    const head = div("hud-weapon__head");
    head.append(this.slotEl, this.nameEl);
    const equip = div("hud-weapon__equip");
    equip.append(this.lethal.root, this.tactical.root);
    this.weapon.append(head, this.ammoBox, this.pipsEl, equip);
    this.hint.hidden = true;
  }

  applySettings(s: Settings): void {
    this.lethal.setKey(keyLabel(s.keybinds.lethal));
    this.tactical.setKey(keyLabel(s.keybinds.tactical));
  }

  /** New life: the loadout (magazine sizes) may have changed. */
  resetAmmoTracking(): void {
    this.maxByWeapon.clear();
    this.wName = null;
  }

  setDead(dead: boolean): void {
    setClass(this.health, "is-dead", dead);
    setClass(this.weapon, "is-dead", dead);
  }

  update(f: HudFrame): void {
    this.updateHealth(f);
    this.updateShield(f.spawnShield);
    this.updateWeapon(f);
    this.lethal.update(f.lethal, f.lethalCount);
    this.tactical.update(f.tactical, f.tacticalCount);
  }

  private updateHealth(f: HudFrame): void {
    const max = f.maxHealth > 0 ? f.maxHealth : 100;
    const hp = f.health > 0 ? Math.ceil(f.health) : 0;
    if (hp !== this.hp) {
      this.hp = hp;
      this.hpNum.textContent = String(hp);
    }
    const frac =
      f.health > 0 ? Math.round(clamp01(f.health / max) * 400) / 400 : 0;
    if (frac !== this.hpFrac) {
      this.hpFrac = frac;
      const t = `scaleX(${frac})`;
      this.hpFill.style.transform = t;
      this.hpTrail.style.transform = t;
    }
    const low = hp > 0 && frac <= LOW_HEALTH_FRAC;
    if (low !== this.low) {
      this.low = low;
      setClass(this.health, "is-low", low);
      setClass(this.stateRoot, "hud--lowhp", low);
    }
  }

  private updateShield(left: number): void {
    const q = left > 0 ? Math.max(1, Math.round(left * 100)) : 0;
    if (q === this.shieldQ) return;
    if ((q === 0) !== (this.shieldQ === 0)) {
      setHidden(this.shieldEl, q === 0);
      setClass(this.health, "is-shielded", q > 0);
    }
    this.shieldQ = q;
    this.shieldFill.style.transform = `scaleX(${q / 100})`;
  }

  private updateWeapon(f: HudFrame): void {
    if (f.weaponName !== this.wName) {
      this.wName = f.weaponName;
      this.nameEl.textContent = f.weaponName;
      this.maxAmmo = this.maxByWeapon.get(f.weaponName) ?? 0;
    }
    if (f.slot !== this.wSlot) {
      this.wSlot = f.slot;
      this.slotEl.textContent = String(f.slot + 1);
    }
    if (f.ammo > this.maxAmmo) {
      this.maxAmmo = f.ammo;
      this.maxByWeapon.set(f.weaponName, f.ammo);
    }
    if (f.ammo !== this.ammo) {
      this.ammo = f.ammo;
      this.magEl.textContent = String(f.ammo);
    }
    if (f.reserve !== this.reserve) {
      this.reserve = f.reserve;
      this.reserveEl.textContent = String(f.reserve);
    }
    // A weapon that never held ammo (the knife) shows no counters.
    const melee = f.ammo === 0 && f.reserve === 0 && this.maxAmmo === 0;
    if (melee !== this.melee) {
      this.melee = melee;
      setClass(this.ammoBox, "is-melee", melee);
    }
    this.updatePips(f.magSize > 0 ? f.magSize : this.maxAmmo, f.ammo, melee);
    const empty = !melee && f.ammo <= 0;
    const full = Math.max(f.magSize, this.maxAmmo);
    const lowAmmo =
      !melee &&
      !empty &&
      f.ammo <= Math.max(1, Math.ceil(full * LOW_AMMO_FRAC));
    if (empty !== this.ammoEmpty) {
      this.ammoEmpty = empty;
      setClass(this.ammoBox, "is-empty", empty);
      setClass(this.pipsEl, "is-empty", empty);
    }
    if (lowAmmo !== this.ammoLow) {
      this.ammoLow = lowAmmo;
      setClass(this.ammoBox, "is-low", lowAmmo);
      setClass(this.pipsEl, "is-low", lowAmmo);
    }
    if (f.reloading !== this.reloading) {
      this.reloading = f.reloading;
      setClass(this.ammoBox, "is-reloading", f.reloading);
      if (!f.reloading) this.reloadQ = -1;
    }
    if (f.reloading) {
      const q = Math.round(clamp01(f.reloadFrac) * 100);
      if (q !== this.reloadQ) {
        this.reloadQ = q;
        this.ringArc.style.strokeDashoffset = String(RING_LEN * (1 - q / 100));
      }
    }
    // Centre hint: an empty magazine (pulsing), a reload in progress, a magazine running low.
    let hint = "";
    if (melee) hint = "";
    else if (f.reloading) hint = "RELOADING";
    else if (empty) hint = f.reserve > 0 ? "RELOAD" : "NO AMMO";
    else if (lowAmmo && f.reserve > 0) hint = "LOW AMMO";
    if (hint !== this.hintText) {
      this.hintText = hint;
      this.hint.textContent = hint;
      this.hint.hidden = hint === "";
      setClass(this.hint, "is-noammo", hint === "NO AMMO");
      setClass(this.hint, "is-reloading", hint === "RELOADING");
      setClass(this.hint, "is-low", hint === "LOW AMMO");
    }
  }

  /** Rebuilds the cells when the magazine size changes and darkens the spent rounds. */
  private updatePips(mag: number, ammo: number, melee: boolean): void {
    const size = melee ? 0 : mag;
    if (size !== this.pipMag) {
      this.pipMag = size;
      this.pips = [];
      for (let i = 0; i < size; i++) this.pips.push(span("hud-pip"));
      this.pipsEl.replaceChildren(...this.pips);
      this.pipsEl.hidden = size === 0;
      setClass(this.pipsEl, "is-dense", size > 20);
      this.pipsLit = size;
    }
    const lit = Math.max(0, Math.min(ammo, size));
    if (lit === this.pipsLit) return;
    const lo = Math.min(lit, this.pipsLit);
    const hi = Math.max(lit, this.pipsLit);
    for (let i = lo; i < hi; i++)
      setClass(this.pips[i] as HTMLElement, "is-spent", i >= lit);
    this.pipsLit = lit;
  }
}

function keyLabel(bindings: readonly string[]): string {
  const b = bindings[0];
  return b ? bindingLabel(b) : "";
}
